import { Action } from '@occc-app/rootRedux';

import types, {
  EXTERNAL_FULFILLMENT,
  LOYALTY_EVENT,
  MEMBERSHIP_EVENT,
  PerGroupPurchasedGroup,
  PerTaggedGroupPurchasedGroup,
  Product,
  PER_GROUP_PURCHASED,
  PER_ITEM_PURCHASED,
  PER_ITEMS_ACCUMULATED,
  PER_TAGGED_GROUP_PURCHASED,
  PER_TAGGED_ITEM_PURCHASED,
  PER_TAGGED_ITEMS_ACCUMULATED,
  PRODUCT_PURCHASE,
  State,
  TOTAL_ELIGIBLE_PURCHASE,
  TOTAL_ELIGIBLE_PURCHASE_OVER_TIME,
  TOTAL_SPEND_ON_TRANSACTION,
  TOTAL_SPEND_OVER_TIME,
  VARIETY_PURCHASE,
  VARIETY_PURCHASE_OVER_TIME,
  VISITS,
} from './types';

import { default as offerTypes } from '../../../redux/types';

const initialPerGroupPurchasedGroup: PerGroupPurchasedGroup = {
  countPer: 'money_amount',
  countThreshold: 0,
  dirty: false,
  groupIndex: 0,
  itemKey: 'upc',
  removed: false,
  summationMode: 'include',
  values: [],
};
const initialPerTaggedGroupPurchasedGroup: PerTaggedGroupPurchasedGroup = {
  countPer: 'money_amount',
  countThreshold: 0,
  dirty: false,
  groupIndex: 0,
  removed: false,
  summationMode: 'include',
  tags: [],
};
const initialProductAttributeEMProduct: Product = {
  countProductsBy: 'money_amount',
  dirty: false,
  eligibleProductGroups: [],
  productIndex: 0,
  purchaseThreshold: 0,
  removed: false,
};
export const initialState: State = {
  selectedAward: null,
  selectedEarningMechanism: null,
  emConfiguration: {
    externalFulfillment: {
      type: EXTERNAL_FULFILLMENT,
    },
    loyaltyEvent: {
      type: LOYALTY_EVENT,
      eventType: '',
    },
    membershipEvent: {
      type: MEMBERSHIP_EVENT,
      eventType: 'created',
    },
    perGroupPurchased: {
      type: PER_GROUP_PURCHASED,
      groups: [initialPerGroupPurchasedGroup],
    },
    perItemsAccumulated: {
      type: PER_ITEMS_ACCUMULATED,
      itemKey: 'upc',
      summationMode: 'include',
      values: [],
      countPer: 'money_amount',
      countThreshold: 0,
    },
    perItemPurchased: {
      type: PER_ITEM_PURCHASED,
      itemKey: 'upc',
      summationMode: 'include',
      values: [],
      countPer: 'money_amount',
      countRounding: 'half_up',
    },
    perTaggedGroupPurchased: {
      type: PER_TAGGED_GROUP_PURCHASED,
      groups: [initialPerTaggedGroupPurchasedGroup],
    },
    perTaggedItemPurchased: {
      type: PER_TAGGED_ITEM_PURCHASED,
      countPer: 'money_amount',
      countRounding: 'down',
      summationMode: 'exclude',
      tags: [],
    },
    perTaggedItemsAccumulated: {
      type: PER_TAGGED_ITEMS_ACCUMULATED,
      countPer: 'money_amount',
      countThreshold: 0,
      summationMode: 'include',
      tags: [],
    },
    productPurchase: {
      type: PRODUCT_PURCHASE,
      countProductsBy: 'money_amount',
      eligibleProductGroups: [],
      purchaseThreshold: 0,
    },
    totalEligiblePurchase: {
      type: TOTAL_ELIGIBLE_PURCHASE,
      countProductsBy: 'money_amount',
      eligibleProductGroups: [],
      roundCountedProducts: 'down',
    },
    totalEligiblePurchaseOverTime: {
      type: TOTAL_ELIGIBLE_PURCHASE_OVER_TIME,
      accumulationThreshold: 0,
      countProductsBy: 'money_amount',
      eligibleProductGroups: [],
    },
    totalSpendOnTransaction: {
      type: TOTAL_SPEND_ON_TRANSACTION,
      ineligibleProductGroups: [],
      roundMoneyAmount: 'down',
    },
    totalSpendOverTime: {
      type: TOTAL_SPEND_OVER_TIME,
      accumulationThreshold: 0,
      ineligibleProductGroups: [],
    },
    varietyPurchase: {
      type: VARIETY_PURCHASE,
      products: [initialProductAttributeEMProduct],
    },
    varietyPurchaseOverTime: {
      type: VARIETY_PURCHASE_OVER_TIME,
      products: [initialProductAttributeEMProduct],
    },
    visits: {
      type: VISITS,
      visitThreshold: 0,
    },
  },
  awardConfiguration: {
    externalPointAward: {
      type: 'externalPointAward',
      maximum: 0,
    },
    pointAward: {
      type: 'pointAward',
      points: 0,
    },
    pointsPerAward: {
      type: 'pointsPerAward',
      points: 0,
      pointRounding: 'half_up',
    },
    nullAward: {
      type: 'nullAward',
    },
  },
  error: null,
  errorMessages: [],
  id: '',
  dirty: false,
  requesting: true,
  submitting: false,
};

export function reducer(state: State = initialState, action: Action): State {
  switch (action.type) {
    case types.EARNING_MECHANISM_COMPONENT_ERR:
      return {
        ...state,
        error: action.error,
        errorMessages: action.errorMessages,
      };
    case types.EARNING_MECHANISM_COMPONENT_OK:
      const { id } = action;
      return {
        ...state,
        id,
        error: null,
        errorMessages: [],
        dirty: false,
      };
    case types.REQUEST_EARNING_MECHANISM:
      return {
        ...state,
        id: action.id,
        dirty: true,
        error: null,
        errorMessages: [],
      };
    case types.REQUEST_EARNING_MECHANISM_ERR:
      return {
        ...state,
        error: action.error,
        errorMessages: action.errorMessages,
      };
    case types.REQUEST_EARNING_MECHANISM_OK:
      const awardType = action.selectedAwardConfiguration.type;
      const emType = action.selectedEMConfiguration.type;
      return {
        ...state,
        error: null,
        errorMessages: [],
        dirty: false,
        requesting: false,
        selectedAward: awardType,
        selectedEarningMechanism: emType,
        awardConfiguration: {
          ...state.awardConfiguration,
          [awardType]: action.selectedAwardConfiguration,
        },
        emConfiguration: {
          ...state.emConfiguration,
          [emType]: action.selectedEMConfiguration,
        },
      };
    case types.REQUEST_EARNING_MECHANISMS_OK:
      return {
        ...state,
        requesting: false,
      };
    case types.RESET_INITIAL_EARNING_MECHANISM_STATE:
      return {
        ...state,
        ...action.earningMechanisms,
      };
    case offerTypes.CLEAR_OFFER_COMPONENT_STATE:
      return initialState;
  }
  return state;
}
